'use client'

import React from 'react'
import { Card, CardProps } from './index'
import { useViewportTracking } from '@/hooks/useViewportTracking'
import { useUser } from '@/context/optionalClerk'

export type TrackableCardProps = CardProps & {
  userId?: string
  itemId?: string
  itemType?: 'product' | 'solution'
  enableTracking?: boolean
}

/**
 * Wrapper around Card component that tracks viewport visibility
 * for analytics purposes. When the card is visible for at least 1 second,
 * it records an analytics event.
 *
 * Tracks both logged-in and anonymous users.
 * Excludes users with tracking disabled (including super-admins).
 */
export const TrackableCard: React.FC<TrackableCardProps> = (props) => {
  const { userId, itemId, itemType, enableTracking = true, ...cardProps } = props
  const { user, isLoaded } = useUser()

  // Check if tracking is disabled for the current user (only applies to logged-in users)
  // Anonymous users (user === null) are allowed to be tracked
  // Exclude super-admins automatically, or users with tracking explicitly disabled
  const roles = (user?.publicMetadata?.payloadRoles as string[] | undefined) ?? []
  const isSuperAdmin = roles.includes('super-admin')
  const isTrackingExplicitlyDisabled = user?.publicMetadata?.isTrackingDisabled ?? false
  const isTrackingDisabled = isSuperAdmin || isTrackingExplicitlyDisabled

  // Only track if we have all required data, tracking is enabled, user is loaded, and user tracking is not disabled
  const shouldTrack = enableTracking && isLoaded && !!userId && !!itemId && !!itemType && !isTrackingDisabled

  const trackingRef = useViewportTracking({
    userId: userId || '',
    itemId: itemId || '',
    itemType: itemType || 'product',
    threshold: 0.5, // 50% of card must be visible
    minVisibilityTime: 1000, // Must be visible for 1 second
    enabled: shouldTrack,
  })

  return (
    <div ref={trackingRef} className="h-full">
      <Card {...(cardProps as any)} />
    </div>
  )
}
